/**
 * @file This file contains the GameController class, which handles player input
 * and orchestrates the game flow based on user interactions.
 * @module GameController
 */

import { EventEmitter } from '../lib/EventEmitter.js';
import { DisasterManager } from '../managers/DisasterManager.js';

export class GameController {
    /**
     * @param {object} dependencies - The dependencies for the controller.
     * @param {import('../Game.js').Game} dependencies.game - The main game instance, used for passing to legacy managers.
     * @param {import('../models/GameState.js').GameState} dependencies.gameState - The model for the game's state.
     * @param {import('../components/Grid.js').Grid} dependencies.grid - The grid component.
     * @param {import('../managers/AnimalManager.js').AnimalManager} dependencies.animalManager - The animal manager.
     * @param {import('../managers/MatchManager.js').MatchManager} dependencies.matchManager - The match manager.
     * @param {object} dependencies.textConfig - The configuration for game text.
     * @param {import('./UIManager.js').UIManager} dependencies.ui - The UI manager instance.
     * @param {function} dependencies.onGameOver - Callback function to handle game over.
     * @param {number} dependencies.totalGridSize - The total size of the grid including buffers.
     * @param {import('../managers/SettlementManager.js').SettlementManager} dependencies.settlementManager - The settlement manager instance.
     * @param {import('../lib/EventEmitter.js').EventEmitter} dependencies.eventEmitter - The event emitter instance.
     * @param {import('../managers/DisasterManager.js').DisasterManager} dependencies.disasterManager - The disaster manager instance.
     */
    constructor({ game, gameState, grid, animalManager, matchManager, textConfig, ui, onGameOver, totalGridSize, eventEmitter, disasterManager, settlementManager }) {
        this.game = game;
        this.gameState = gameState; 
        this.grid = grid;
        this.animalManager = animalManager;
        this.matchManager = matchManager;
        this.textConfig = textConfig;
        this.ui = ui;
        this.onGameOver = onGameOver;
        this.totalGridSize = totalGridSize;
        this.settlementManager = settlementManager;
        this.eventEmitter = eventEmitter;
        this.disasterManager = disasterManager;
        this.isProcessingTurn = false;

        this.boundHandleCellClick = this.handleCellClick.bind(this);
        /** @private @type {'none' | 'place_settlement' | 'delete_cell'} */
        this.currentTool = 'none';
        /** @private @type {string|null} */
        this.selectedSettlementKey = null;
    }

    /**
 * Description placeholder
 *
 * @type {boolean}
 */
isPaused = false;

    /**
     * Sets the controller into placement mode for a specific settlement type.
     * @param {string} settlementKey - The key of the settlement to place.
     */
    setPlacementMode(settlementKey) {
        this.currentTool = 'place_settlement';
        this.selectedSettlementKey = settlementKey;
        this.ui.setActiveRtsTool(this.currentTool, this.selectedSettlementKey);
        this.setPauseState(true); // Automatically pause when entering placement mode
        this.ui.displayMessage(`Click on the grid to place a ${this.game.configManager.masterItemsConfig.settlements[settlementKey].name}.`);
        this.grid.domElement.classList.add('tool-active');
    }

    /**
     * Sets the controller into delete mode.
     */
    setDeleteMode() {
        this.currentTool = 'delete_cell';
        this.selectedSettlementKey = null; // Clear any selected settlement
        this.ui.setActiveRtsTool(this.currentTool, null);
        this.setPauseState(true); // Automatically pause when entering delete mode
        this.ui.displayMessage('Click on any cell to delete its content.');
        this.grid.domElement.classList.add('tool-active');
    }

    /**
     * Resets the controller's tool mode to none.
     */
    resetToolMode() {
        this.currentTool = 'none';
        this.selectedSettlementKey = null;
        this.ui.setActiveRtsTool(this.currentTool, null);
        this.grid.domElement.classList.remove('tool-active');

        // In Settlers Mode, the game is always "paused" from a turn-based perspective.
        // We don't need to change the pause state, just update the message.
        if (this.gameState.activeGameConfig.isSettlersModeEnabled) {
            this.ui.displayMessage('Select a building to place or press the play icon to resume time.');
        }
        if (this.isPaused) {
            this.ui.displayMessage('Game paused. Select a building or press the play icon to resume.');
        }
    }

    /**
     * Toggles the placement mode for a specific settlement type.
     * If the same settlement is clicked again, it deactivates the mode.
     * @param {string} settlementKey - The key of the settlement to toggle placement mode for.
     */
    togglePlacementMode(settlementKey) {
        if (this.currentTool === 'place_settlement' && this.selectedSettlementKey === settlementKey) {
            this.resetToolMode();
        } else {
            this.setPlacementMode(settlementKey);
        }
    }

    /**
     * Toggles the delete mode.
     * If delete is clicked again, it deactivates the mode.
     */
    toggleDeleteMode() {
        if (this.currentTool === 'delete_cell') {
            this.resetToolMode();
        } else {
            this.setDeleteMode();
        }
    }

    /**
     * Sets the paused state of the game and updates the UI.
     * @param {boolean} shouldBePaused - True to pause the game, false to resume.
     */
    setPauseState(shouldBePaused) {
        if (this.isPaused === shouldBePaused) return; // No change needed

        // In Settlers Mode, the game is always paused in terms of turns.
        // This method only controls the time-based updates (NPCs, settlements).
        this.isPaused = shouldBePaused;
        if (this.isPaused) {
            this.ui.displayMessage('Game paused. Place your building or press the play icon to resume.');
        } else if (!this.gameState.activeGameConfig.isSettlersModeEnabled) {
            this.ui.displayMessage('Game resumed.');
        }
        // Update the button icon in the UI
        this.ui.updateRtsPauseButton(this.isPaused);
    }

    /**
     * Toggles the paused state of the game, preventing grid interactions.
     */
    togglePauseMode() {
        // In Settlers Mode, this button's only job is to toggle the time-based updates.
        if (this.gameState.activeGameConfig.isSettlersModeEnabled) {
            this.isPaused = !this.isPaused;
            this.ui.updateRtsPauseButton(this.isPaused);
            return;
        }
        // If we are in a tool mode, toggling pause should exit the tool mode first.
        if (this.currentTool !== 'none') this.resetToolMode();
        this.setPauseState(!this.isPaused);
    }

    /**
     * Initializes the controller, setting up event listeners.
     */
    initController() {
        this.ui.createGrid(this.grid, this.totalGridSize, this.boundHandleCellClick, this.gameState.activeGameConfig.gameMode, this.gameState.activeGameConfig.isSettlersModeEnabled);
        this.eventEmitter.on('game:setIsProcessingTurn', (value) => {
            this.isProcessingTurn = value;
        });
        this.eventEmitter.on('game:startDisaster', (disasterKey) => {
            this.disasterManager.startDisaster(disasterKey);
        });
        this.eventEmitter.on('debug:addScore', (amount) => {
            if (this.gameState.activeGameConfig.isMiniMerge) {
                console.warn('Scoring is not implemented for Mini Merge mode yet.');
                return;
            }

            if (this.gameState) {
                this.gameState.addScore(amount);
                this.ui.updateScore(this.gameState.score);
            }
        });
    }

    /**
     * Cleans up the controller by removing its event listeners.
     */
    destroy() {
        this.eventEmitter.off('game:setIsProcessingTurn');
        this.eventEmitter.off('game:startDisaster');
        this.eventEmitter.off('debug:addScore');
    }

    /**
     * Handles the logic when a grid cell is clicked.
     * @param {Event} event - The click event from the UI.
     */
    handleCellClick(event) {
        if (this.gameState.isGameOver || this.isProcessingTurn) return;


      // NEW: Handle NPC Control Mode
        if (this.game.isNpcControlMode) {
            const t = event.target.closest('.gem');
            if (!t) return;
            const r = parseInt(t.dataset.row);
            const c = parseInt(t.dataset.col);
            this.handleNpcControlClick(r, c);
            return;
        }

        // In rolling mode, grid clicks are disabled. Player controls the collector.
        if (this.gameState.activeGameConfig.gameMode === 'rolling') {
            this.ui.displayMessage('Move your collector to catch the items!');
            return;
        }

        const cellElement = event.target.closest('.gem');
        if (!cellElement) return; // Click was not on a cell or its content
        const row = parseInt(cellElement.dataset.row);
        const col = parseInt(cellElement.dataset.col);

        // Handle RTS tool modes first
        // Check if the clicked cell contains a settlement and open its editor
        const item = this.grid.get(row, col);
        if (item && item.isStationary && item.recipes) { // Check for recipes to confirm it's a settlement
            // Pass the full settlement object, not just the key
            const settlement = this.settlementManager.getSettlementAt(row, col);
            if (settlement) this.eventEmitter.emit('ui:requestSettlementEditor', { settlement });
            return; // Stop further processing as the editor is opened
        }

        if (this.currentTool === 'place_settlement') {
            this._placeSettlement(row, col, this.selectedSettlementKey);
            this.resetToolMode(); // Reset tool after placement
            return;
        } else if (this.currentTool === 'delete_cell') {
            this._deleteCell(row, col);
            this.resetToolMode(); // Reset tool after deletion
            return;
        }

        // If the game is paused, block placing items from the regular queue.
        if (this.isPaused) {
            this.ui.displayMessage('Game is paused. Click the play icon to place items from the queue.');
            return;
        }

        this._performClick(row, col);
    }

/**
     * Handles a grid click when in NPC control mode.
     * @param {number} r - The clicked row.
     * @param {number} c - The clicked column.
     */
    handleNpcControlClick(r, c) {
        if (!this.grid.isPlayable(r, c)) {
            this.ui.displayMessage('Cannot move NPC outside the playable area.');
            return;
        }

        const manager = this.game.controlledNpcManagerType === 'human'
            ? this.game.npcManager
            : this.game.nonHumanNPCManager;

        if (!manager) return;

        const npc = manager.npcs.get(this.game.controlledNpcId);
        if (!npc) return;

        // Check if the destination is a settlement
        const settlement = this.settlementManager.getSettlementAt(r, c);
        if (settlement) {
            npc.state.interactionTarget = settlement; // Set the specific interaction target
            this.ui.displayMessage(`Ordering NPC to interact with ${settlement.name}...`);
        } else {
            npc.state.interactionTarget = null; // Clear any previous interaction target
        }

        const path = this.grid.findPath(npc.position, { r, c }, this.game.obstacleManager);

        if (path && path.length > 0) {
            npc.setPath(path);
            this.ui.displayMessage('Moving NPC to target...');
        } else {
            this.ui.displayMessage('Cannot find a path to that location.');
        }
    }

    /**
     * Placeholder for handling queue clicks. Overridden in MiniMergeController.
     * @param {number} index - The index of the clicked item in the queue.
     */
    handleQueueClick(index) {
        // This is intentionally left empty for the base controller.
    }
    /**
     * Allows the DemoManager to trigger a click programmatically.
     * @param {number} row - The row to click.
     * @param {number} col - The column to click.
     */
    performProgrammaticClick(row, col) {
        this._performClick(row, col);
    }

    /**
 * Description placeholder
 *
 * @param {*} row 
 * @param {*} col 
 */
_performClick(row, col) {
        if (!this.grid.isPlayable(row, col)) {
            return;
        }

        this.isProcessingTurn = true;



        const nextItem = this.gameState.itemQueue[0];

        if (nextItem.isPowerUp) {
            const itemToPlace = this.gameState.itemQueue.shift();
            if (this.handlePowerUp(itemToPlace, row, col)) {
                this.gameState.itemQueue.push(this.gameState.getRandomQueueItem());
            }
            this.eventEmitter.emit('game:powerupPlaced', { key: itemToPlace.key, row, col });            
            this.endTurn(); // A power-up use counts as a full turn.
            
            this.isProcessingTurn = false;
        } else {
            const cellItem = this.grid.get(row, col);
            if (cellItem !== null) {
                const message = this.gameState.activeGameConfig.animals.some(a => a.key === cellItem.key) ?
                    this.textConfig.gameMessages.placeOnAnimal.replace('{ANIMAL_NAME}', this.animalManager.activeAnimal.name) :
                    this.textConfig.gameMessages.cellNotEmpty;
                this.ui.displayMessage(message);
                this.isProcessingTurn = false;
                return;
            }

            const itemToPlace = this.gameState.itemQueue.shift();
            this.grid.set(row, col, itemToPlace);
            this.game.gemCount++; // Increment gem count for the newly placed item.
            this.gameState.itemQueue.push(this.gameState.getRandomQueueItem());
            this.eventEmitter.emit('game:itemPlaced', { item: itemToPlace, row, col });
            this.ui.updateGrid(this.grid.data, this.totalGridSize, this.gameState.activeGameConfig);            
            setTimeout(() => this.processTurn(row, col), 200);
        }

    }

    /**
     * Places a settlement on the grid, bypassing normal game rules.
     * @param {number} row - The row to place the settlement.
     * @param {number} col - The column to place the settlement.
     * @param {string} settlementKey - The key of the settlement to place.
     * @private
     */
    _placeSettlement(row, col, settlementKey) {
        if (!this.grid.isPlayable(row, col)) {
            this.ui.displayMessage('Cannot place settlement outside playable area.');
            return;
        }
        if (this.grid.get(row, col) !== null) {
            this.ui.displayMessage('Cannot place settlement on an occupied cell.');
            return;
        }

        const settlementConfig = this.game.configManager.masterItemsConfig.settlements[settlementKey];
        if (!settlementConfig) {
            this.ui.displayMessage('Invalid settlement selected.');
            return;
        }

        this.game.settlementManager.createSettlement(row, col, settlementConfig);
        this.ui.updateGrid(this.grid.data, this.totalGridSize, this.gameState.activeGameConfig);
        this.ui.displayMessage(`${settlementConfig.name} placed at (${row}, ${col}).`);
    }

    /**
     * Deletes the content of a cell, bypassing normal game rules.
     * @param {number} row - The row of the cell to delete.
     * @param {number} col - The column of the cell to delete.
     * @private
     */
    _deleteCell(row, col) {
        if (!this.grid.isPlayable(row, col)) {
            this.ui.displayMessage('Cannot delete outside playable area.');
            return;
        }
        if (this.grid.get(row, col) === null) {
            this.ui.displayMessage('Cell is already empty.');
            return;
        }

        const item = this.grid.get(row, col);
        if (!item) {
            this.ui.displayMessage('Cell is already empty.');
            return;
        }

        // Notify the correct manager based on the item's key/type
        if (item.key?.startsWith('settlement_')) {
            this.game.settlementManager.removeSettlement(row, col);
        } else if (this.game.configManager.masterItemsConfig.npcs[item.key]) {
            this.game.npcManager.removeNpcAt(row, col);
        } else if (this.game.configManager.masterItemsConfig.nonHumanNpcs[item.key]) {
            this.game.nonHumanNPCManager.removeNpcAt(row, col);
        } else if (this.animalManager) {
            this.animalManager.removeAnimalAt(row, col);
        }

        this.grid.set(row, col, null);
        this.ui.updateGrid(this.grid.data, this.totalGridSize, this.gameState.activeGameConfig);
        this.ui.displayMessage(`Cell at (${row}, ${col}) cleared.`);
        this.ui.addTemporaryEffect(row, col, 'cleaned'); // Visual feedback
        this.ui.playAudio('cleaned'); // Audio feedback
    }

    /**
     * Places a settlement on the grid, bypassing normal game rules.
     * @param {number} row - The row to place the settlement.
     * @param {number} col - The column to place the settlement.
     * @param {string} settlementKey - The key of the settlement to place.
     * @private
     */
    _placeSettlement(row, col, settlementKey) {
        if (!this.grid.isPlayable(row, col)) {
            this.ui.displayMessage('Cannot place settlement outside playable area.');
            return;
        }
        if (this.grid.get(row, col) !== null) {
            this.ui.displayMessage('Cannot place settlement on an occupied cell.');
            return;
        }

        const settlementConfig = this.game.configManager.masterItemsConfig.settlements[settlementKey];
        if (!settlementConfig) {
            this.ui.displayMessage('Invalid settlement selected.');
            return;
        }

        this.game.settlementManager.createSettlement(row, col, settlementConfig);
        this.ui.updateGrid(this.grid.data, this.totalGridSize, this.gameState.activeGameConfig);
        this.ui.displayMessage(`${settlementConfig.name} placed at (${row}, ${col}).`);
    }

    /**
     * Moves the collector in the specified direction if in Rolling Grid mode.
     * @param {'left' | 'right'} direction - The direction to move the collector.
     */
    moveCollector(direction) {
        if (this.gameState.isGameOver || this.isProcessingTurn || this.gameState.activeGameConfig.gameMode !== 'rolling') {
            return;
        }

        const start = this.grid.bufferSize;
        const end = this.grid.size - this.grid.bufferSize;
        let newPosition = this.gameState.collectorPosition;

        if (direction === 'left') {
            newPosition = Math.max(start, newPosition - 1);
        } else if (direction === 'right') {
            newPosition = Math.min(end - 1, newPosition + 1);
        }

        this.gameState.collectorPosition = newPosition;
        this.ui.updateCollector(this.gameState.collectorPosition, this.gameState.collectorBucket, this.grid.bufferSize);
    }
    /**
     * Handles a single turn in the Rolling Grid game mode.
     * This will be triggered by player actions like moving the collector.
     */
    handleRollingTurn() {
        if (this.gameState.isGameOver || this.isProcessingTurn) return;
        if (this.gameState.activeGameConfig.gameMode !== 'rolling') {
            this.ui.displayMessage('This action is only for Rolling Grid mode.');
            return;
        }

        this.isProcessingTurn = true;
        this._handleRollingGridTurn();
        this.endTurn();
    }

    /**
     * Executes the "rolling" mechanic for the grid.
     * @private
     */
    _handleRollingGridTurn() {
        const start = this.grid.bufferSize;
        const end = this.grid.size - this.grid.bufferSize;
        const collectorPos = this.gameState.collectorPosition;

        // 1. Catch falling item
        const lastRow = end - 1;
        const caughtItem = this.grid.get(lastRow, collectorPos);
        if (caughtItem) {
            this.gameState.collectorBucket.unshift(caughtItem); // Add to the top of the stack
            if (this.gameState.collectorBucket.length > this.gameState.COLLECTOR_CAPACITY) {
                this.gameState.collectorBucket.pop(); // Remove the oldest item from the bottom
            }
            // Add a small score for catching an item
            const baseScore = this.gameState.activeGameConfig.scoring.baseMerge || 10;
            this.gameState.addScore(baseScore);
            this.ui.updateScore(this.gameState.score);

            this._handleBucketMerge();
        }

        // 1.5. Animate items that fall off the grid
        for (let c = start; c < end; c++) {
            if (c !== collectorPos) {
                const fallingItem = this.grid.get(lastRow, c);
                if (fallingItem) {
                    // Trigger a visual effect for the falling item
                    this.ui.createFallingItemEffect(fallingItem, lastRow, c);
                }
            }
        }

        // 2. Shift all items down by one row
        for (let r = end - 1; r >= start; r--) {
            for (let c = start; c < end; c++) {
                const itemAbove = this.grid.get(r - 1, c);
                this.grid.set(r, c, itemAbove);
            }
        }

        // 3. Add a new, partially filled row at the top
        const topPlayableRow = start;
        for (let c = start; c < end; c++) {
            // 50% chance to spawn a new item in each cell of the new row
            if (Math.random() < 0.5) {
                const newItem = this.gameState.getRandomQueueItem(false); // No power-ups
                this.grid.set(topPlayableRow, c, newItem);
            } else {
                this.grid.set(topPlayableRow, c, null);
            }
        }

        // 4. Update UI
        this.ui.updateGrid(this.grid.data, this.totalGridSize, this.gameState.activeGameConfig);
        this.ui.updateCollector(this.gameState.collectorPosition, this.gameState.collectorBucket, this.grid.bufferSize);
    }

    /**
     * Handles merging of items within the collector bucket.
     * @private
     */
    _handleBucketMerge() {
        const bucket = this.gameState.collectorBucket;
        if (bucket.length < 3) return;

        for (let i = 0; i <= bucket.length - 3; i++) {
            const item1 = bucket[i];
            const item2 = bucket[i + 1];
            const item3 = bucket[i + 2];

            if (item1 && item2 && item3 && item1.key === item2.key && item1.key === item3.key) {
                const upgradePath = this.gameState.activeGameConfig.upgradePath;
                const itemIndex = upgradePath.findIndex(item => item.key === item1.key);
                if (itemIndex > -1 && itemIndex < upgradePath.length - 1) {
                    const newItem = upgradePath[itemIndex + 1];
                    bucket.splice(i, 3, newItem);
                    const scoreToAdd = this.gameState.calculateMergeScore(3, itemIndex, false);
                    this.gameState.addScore(scoreToAdd);
                    this.ui.updateScore(this.gameState.score);
                    this.ui.playAudio('merge');
                    // After a merge, we should re-check from the beginning
                    this._handleBucketMerge(); 
                    return;
                }
            }
        }
    }

    /**
     * Handles the application of power-up effects.
     * @param {object} item - The power-up item to handle.
     * @param {number} row - The row where the power-up was used.
     * @param {number} col - The column where the power-up was used.
     * @returns {boolean} True if the power-up was successfully applied, false otherwise.
     */
    handlePowerUp(item, row, col) {
        switch (item.key) {
        case 'powerup_cleanup': {
            const radius = 1;
            let clearedCellsCount = 0;
            for (let r = row - radius; r <= row + radius; r++) {
                for (let c = col - radius; c <= col + radius; c++) {
                    if (this.grid.isPlayable(r, c)) {
                        if (this.grid.get(r, c) !== null) {
                            clearedCellsCount++;
                        }
                        if (this.animalManager) this.animalManager.removeAnimalAt(r, c);
                        this.ui.addTemporaryEffect(r, c, 'cleaned');
                    }
                }
            }

            if (clearedCellsCount === 9) {
                this.eventEmitter.emit('achievement:perfectPowerup', { powerupKey: item.key, cellsCleared: 9, row, col });
            }

            if (clearedCellsCount > 0) {
                this.ui.playAudio('cleaned');
            }

            this.grid.clearArea(row, col, radius);

            const scoringConfig = this.gameState.activeGameConfig.scoring;
            const baseScore = clearedCellsCount * scoringConfig.powerupClearBase;
            const bonusScore = (clearedCellsCount === 9) ? scoringConfig.powerupClearBonus : 0;
            const totalScore = baseScore + bonusScore;

            if (totalScore > 0) {
                this.gameState.addScore(totalScore);
                this.ui.updateScore(this.gameState.score);
            }

            this.ui.updateGrid(this.grid.data, this.totalGridSize, this.gameState.activeGameConfig);
            this.ui.displayMessage('Whoosh! Area swept clean.');
            this.gameState.incrementPowerupsUsed();
            this.eventEmitter.emit('achievement:powerupUsed', { powerupKey: item.key, row, col });
            break;
        }

        case 'powerup_pickaxe':
            const cellItem = this.grid.get(row, col);
            if (cellItem) {
                if (this.animalManager) this.animalManager.removeAnimalAt(row, col);
                this.grid.set(row, col, null); // Remove whatever is there
                this.gameState.addScore(this.gameState.activeGameConfig.scoring.powerupClearBase);
                this.ui.updateScore(this.gameState.score);
                this.ui.updateGrid(this.grid.data, this.totalGridSize, this.gameState.activeGameConfig);
                this.ui.displayMessage('Item removed!');
                this.ui.addTemporaryEffect(row, col, 'dust');
                this.ui.playAudio('dust');
                this.gameState.incrementPowerupsUsed();
                this.eventEmitter.emit('achievement:powerupUsed', { powerupKey: item.key, row, col });
            } else {
                this.ui.displayMessage('Nothing to remove here.');
                this.gameState.itemQueue.unshift(item);
                return false;
            }
            break;
        }
        return true;
    }

    /**
     * Starts the match processing for a specific turn.
     * @param {number} row - The row of the placed item.
     * @param {number} col - The column of the placed item.
     */
    processTurn(row, col) {
        this.matchManager.processMatches(this.gameState, row, col, () => this.startTurnResolution());
    }

    /**
     * Called after matching is complete to continue the turn.
     */
    startTurnResolution() {
        this.handleTurnLogic();
    }

    /**
     * Handles the main logic flow of a turn after the initial placement and match.
     */
    handleTurnLogic() {
        if (!this.animalManager) {
            this.endTurn();
            return;
        }

        const trapResult = this.animalManager.checkAndTrapAnimals();
        if (trapResult.newlyTrappedItems.length > 0) {
            this.gameState.incrementCratesBuilt(trapResult.newlyTrappedItems.length);

            // Emit events for achievements
            for (let i = 0; i < trapResult.newlyTrappedItems.length; i++) {
                this.eventEmitter.emit('achievement:animalCrated', { gameState: this.gameState });
            }
            this.eventEmitter.emit('achievement:sessionCrateCountUpdated', { crateCount: this.gameState.cratesBuilt, gameState: this.gameState });

            this.gameState.addScore(trapResult.score);
            this.ui.updateScore(this.gameState.score);
            this.ui.updateGrid(this.grid.data, this.totalGridSize, this.gameState.activeGameConfig);
            let trappedItemIndex = 0;
            this.ui.playAudio('crate');
            const processNextTrappedItem = () => {
                if (trappedItemIndex < trapResult.newlyTrappedItems.length) {
                    const pos = trapResult.newlyTrappedItems[trappedItemIndex++];
                    this.matchManager.processMatches(this.gameState, pos.r, pos.c, processNextTrappedItem);
                } else {
                    this.handleTurnLogic();
                }
            };
            setTimeout(processNextTrappedItem, 200);
            return;
        }

        this.animalManager.handleMovement((animalsMoved) => {
            if (animalsMoved) {
                setTimeout(() => this.handleTurnLogic(), 200);
            } else {
                const animalSpawned = this.animalManager.handleSpawning();
                if (this.grid.getEmptyCellCount() === 0) {
                    this.onGameOver();
                } else {
                    this.endTurn();
                }
            }
        });
    }

    /**
     * Finalizes the turn by updating disasters and allowing player input.
     * @private
     */
    endTurn() {        
        if (this.gameState.activeGameConfig.isMiniMerge) {
            this.isProcessingTurn = false;
            return;
        }

        // --- All turn-based logic happens here, BEFORE the UI is updated ---

        // 1. Decrement animal counters
        if (this.animalManager) {
            this.animalManager.decrementCounters();
        }

        // 2. Update general turn counters
        this.gameState.numberOfTurnsTaken++;
        if (this.gameState.isLimitedTurnsMode) {
            this.gameState.numberOfTurnsRemaining--;
        }

        // 3. Update disaster manager
        this.disasterManager.update();

        // 4. Emit turn ended event for achievements
        this.eventEmitter.emit('achievement:turnEnded', { gameState: this.gameState });

        // 5. Trigger autosave every 10 turns
        if (this.gameState.numberOfTurnsTaken > 0 && this.gameState.numberOfTurnsTaken % 10 === 0) {
            this.game.autoSave();
        }

        // --- Now, update the UI with the new state for the *next* turn ---
        const turnsUntilAnimal = this.animalManager ? this.animalManager.getTurnsUntilNextSpawn() : -1;
        const nextAnimal = this.animalManager ? this.animalManager.getNextAnimalToSpawn() : null;
        this.ui.updateQueue(this.gameState.itemQueue, turnsUntilAnimal, nextAnimal, this.gameState.activeGameConfig.gameMode);

        if (this.gameState.isLimitedTurnsMode) {
            this.ui.updateNumberOfTurns(this.gameState.numberOfTurnsRemaining);
        } else {
            this.ui.updateNumberOfTurns(this.gameState.numberOfTurnsTaken);
        }

        this.isProcessingTurn = false;
        setTimeout(() => {
            this.ui.displayMessage(this.textConfig.gameMessages.nextPiece);
        }, 200);
    }

}